import { publishingDirections } from '@/mocks/home';
import { clamp, TRANSITION, COLORS } from './progress';

const directionImages: Record<string, string> = {
  'dir-tea': 'https://readdy.ai/api/search-image?query=Dreamy%20celestial%20tea%20culture%20scene%20with%20soft%20warm%20amber%20moonlight%2C%20ceramic%20teapot%20and%20teacup%20on%20light%20wooden%20table%2C%20gentle%20misty%20atmosphere%2C%20elegant%20scholarly%20aesthetic%2C%20dreamy%20and%20serene%20composition%2C%20no%20people%2C%20clean%20minimalist%20style&width=800&height=500&seq=dir-tea-x4&orientation=landscape',
  'dir-business': 'https://readdy.ai/api/search-image?query=Dreamy%20celestial%20modern%20workspace%20with%20soft%20warm%20amber%20moonlight%2C%20open%20notebook%20and%20pen%20on%20clean%20wooden%20desk%2C%20gentle%20misty%20atmosphere%2C%20professional%20yet%20calm%20editorial%20composition%2C%20dreamy%20and%20serene%2C%20no%20people%2C%20minimalist%20style&width=800&height=500&seq=dir-business-x4&orientation=landscape',
  'dir-science': 'https://readdy.ai/api/search-image?query=Dreamy%20celestial%20scientific%20illustration%20with%20soft%20warm%20amber%20moonlight%2C%20vintage%20botanical%20prints%20and%20magnifying%20glass%20on%20light%20wood%20surface%2C%20gentle%20misty%20atmosphere%2C%20scholarly%20aesthetic%2C%20dreamy%20and%20serene%2C%20no%20people%2C%20clean%20minimalist%20composition&width=800&height=500&seq=dir-science-x4&orientation=landscape',
  'dir-health': 'https://readdy.ai/api/search-image?query=Dreamy%20celestial%20wellness%20scene%20with%20soft%20warm%20amber%20moonlight%2C%20fresh%20green%20plant%20and%20open%20book%20on%20light%20wooden%20surface%2C%20gentle%20misty%20atmosphere%2C%20peaceful%20healing%20aesthetic%2C%20dreamy%20and%20serene%2C%20no%20people%2C%20clean%20minimalist%20composition&width=800&height=500&seq=dir-health-x4&orientation=landscape',
  'dir-psych': 'https://readdy.ai/api/search-image?query=Dreamy%20celestial%20reading%20nook%20with%20soft%20warm%20amber%20moonlight%2C%20soft%20throw%20blanket%20and%20stacked%20books%20on%20wooden%20floor%2C%20gentle%20misty%20atmosphere%2C%20introspective%20and%20calming%2C%20dreamy%20and%20serene%2C%20no%20people%2C%20cozy%20minimalist%20composition&width=800&height=500&seq=dir-psych-x4&orientation=landscape',
  'dir-humanities': 'https://readdy.ai/api/search-image?query=Dreamy%20celestial%20library%20corner%20with%20soft%20warm%20amber%20moonlight%2C%20vintage%20books%20and%20reading%20lamp%20on%20dark%20wood%20desk%2C%20gentle%20misty%20atmosphere%2C%20intellectual%20and%20timeless%2C%20dreamy%20and%20serene%2C%20no%20people%2C%20clean%20minimalist%20composition&width=800&height=500&seq=dir-humanities-x4&orientation=landscape',
};

interface DirectionsSceneProps {
  progress: number;
}

export default function DirectionsScene({ progress }: DirectionsSceneProps) {
  const p = clamp(progress, 0, 1);
  const enter = clamp(p * 3, 0, 1);
  const exit = 1 - clamp((p - 0.82) * 5.5, 0, 1);
  const opacity = Math.min(enter, exit);
  const translateY = (1 - enter) * 60;

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col items-center justify-center px-5 md:px-8 pt-20 md:pt-24 pb-10 pointer-events-none"
      style={{ opacity, transform: `translateY(${translateY}px)`, transition: TRANSITION, willChange: 'transform, opacity' }}
    >
      {/* 標題 */}
      <div className="text-center mb-5 md:mb-6 max-w-2xl">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="w-10 h-px bg-white/40" />
          <span className="font-manrope text-white/70 text-[10px] uppercase tracking-[0.35em]">
            Vol. 02 — 出版方向
          </span>
          <span className="w-10 h-px bg-white/40" />
        </div>
        <h2 className="font-semibold text-white text-xl md:text-3xl mb-2">
          主要出版方向
        </h2>
        <p className="font-manrope text-xs md:text-sm text-white/60 leading-relaxed">
          策點文化以跨領域精選書籍為主要出版方向，涵蓋茶文化、商業、科普、醫療保健、心理學與人文社科。
        </p>
      </div>

      {/* 六個方向卡片 */}
      <div className="w-full max-w-4xl grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-3">
        {publishingDirections.map((dir, i) => (
          <div
            key={dir.id}
            className="group overflow-hidden border"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderColor: COLORS.line }}
          >
            <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <img
                src={directionImages[dir.id]}
                alt={`策點文化主要出版方向 — ${dir.title}`}
                title={`策點文化 ${dir.title} 出版方向`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(7,18,48,0.7), rgba(7,18,48,0))' }} />
              <span
                className="absolute bottom-2 left-2 w-6 h-6 flex items-center justify-center rounded-md text-[10px] font-michroma"
                style={{ backgroundColor: 'rgba(10,26,58,0.7)', color: '#fff' }}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
            </div>
            <div className="p-2.5 md:p-3">
              <h3 className="font-semibold text-white text-sm md:text-base mb-1">
                {dir.title}
              </h3>
              <p className="font-manrope text-[11px] md:text-xs text-white/55 leading-relaxed">
                {dir.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      <p className="font-manrope text-[11px] text-white/45 text-center mt-4 md:mt-5 max-w-xl leading-relaxed">
        出版內容涵蓋多種知識領域，屬於出版與閱讀資訊，不代表提供各領域的專業顧問、醫療、心理、投資或其他專業服務。
      </p>
    </div>
  );
}